/**
 * Hook amélioré pour la fiabilité avec les corrections Viguen
 * Combine toutes les nouvelles validations pour le composant ReliabilityScore existant
 */

import { useCallback } from 'react';
import { useReportReliability } from './useReportReliability';

interface EnhancedReliabilityResult {
  score: number;
  checks: {
    emailVerified: boolean;
    ipDiversity: boolean;
    charterSigned: boolean;
    deviceDiversity: boolean;
    dateCoherence: boolean;
  };
  // Informations supplémentaires pour debugging (optionnel)
  debugInfo?: {
    fraudDetection?: {
      level: 'low' | 'medium' | 'high';
      reasons: string[];
      penalty: number;
    };
    emailCoherenceScore: number;
    dateReliabilityScore: number;
  };
}

export function useEnhancedReliability() {
  const {
    calculateReliabilityScore,
    getReferentsFromCampaign,
    getCandidateFingerprint,
    getCriticalResponses,
    getDateReliabilityAnalysis,
    getEmailCoherenceAnalysis
  } = useReportReliability();

  /**
   * Calcule la fiabilité améliorée avec toutes les nouvelles validations
   */
  const calculateEnhancedReliability = useCallback(async (campaignId: string): Promise<EnhancedReliabilityResult> => {
    try {
      // 1. Récupérer toutes les données nécessaires
      const [
        referents,
        candidateFingerprint,
        dateAnalysis,
        emailAnalysis
      ] = await Promise.all([
        getReferentsFromCampaign(campaignId),
        getCandidateFingerprint(campaignId),
        getDateReliabilityAnalysis(campaignId),
        getEmailCoherenceAnalysis(campaignId)
      ]);

      // 2. Calculer le score de fiabilité avec les nouvelles validations
      const reliabilityResult = calculateReliabilityScore(
        referents,
        dateAnalysis.averageScore,
        emailAnalysis.overallScore,
        candidateFingerprint
      );

      // 3. Préparer le résultat pour le composant ReliabilityScore existant
      const result: EnhancedReliabilityResult = {
        score: reliabilityResult.score,
        checks: reliabilityResult.checks, // Les checks corrigés (device/IP maintenant précis)
        debugInfo: {
          fraudDetection: reliabilityResult.fraudDetection,
          emailCoherenceScore: emailAnalysis.overallScore,
          dateReliabilityScore: dateAnalysis.averageScore
        }
      };

      return result;
    } catch (_error) {
      console.error('Erreur calcul fiabilité améliorée:', _error);
      
      // Fallback en cas d'erreur
      return {
        score: 0,
        checks: {
          emailVerified: false,
          ipDiversity: false,
          charterSigned: false,
          deviceDiversity: false,
          dateCoherence: false
        }
      };
    }
  }, [
    calculateReliabilityScore,
    getReferentsFromCampaign,
    getCandidateFingerprint,
    getDateReliabilityAnalysis,
    getEmailCoherenceAnalysis
  ]);

  /**
   * Teste les corrections avec le cas Viguen
   */
  const testViguenCorrections = useCallback(async () => {
    // Simuler le cas Viguen pour tester les corrections
    const mockReferents = [
      {
        id: 'viguen_ref',
        name: 'Référent Test',
        email: 'referent@company.com',
        position: 'Manager',
        status: 'Complété',
        company: 'WIS Groupe',
        ipAddress: '192.168.1.100', // Même IP que candidat
        deviceFingerprint: 'viguen_device_123', // Même device que candidat
        isEmailProfessional: true,
        hasSigned: true
      }
    ];

    const mockCandidateFingerprint = {
      deviceFingerprint: 'viguen_device_123', // Même device !
      ipAddress: '192.168.1.100' // Même IP !
    };

    // Test avec les nouvelles validations
    const result = calculateReliabilityScore(
      mockReferents,
      3, // Score date faible (cas 2019 vs 2019-2024)
      85, // Score email correct
      mockCandidateFingerprint
    );

    

    return result;
  }, [calculateReliabilityScore]);

  return {
    calculateEnhancedReliability,
    testViguenCorrections,
    // Exposer les fonctions individuelles si nécessaire
    getReferentsFromCampaign,
    getCandidateFingerprint,
    getDateReliabilityAnalysis,
    getEmailCoherenceAnalysis
  };
}